import { Button } from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { FC } from "react";
import { IconRxDietLogoPurple } from "src/constants/icons";
import { APP_BREAKPOINT } from "src/constants/layout";
import { rxDietDescriptionListItems } from "src/constants/rxDietData";
import rxDietDescriptionDialogImgDesktop from "src/ui/assets/images/rxdiet-description-dialog-img-desktop.jpg";
import rxDietDescriptionDialogImgMobile from "src/ui/assets/images/rxdiet-description-dialog-img-mobile.jpg";
import Translate from "src/ui/components/Translate/Translate";
import { Container, List, ListElement, ListWrapper } from "src/ui/styled/List";
import { StyledPill } from "./RxDietFirstScreen";
import { DisableAppPadding } from "src/ui/components/AppRouter/AppRouter";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100%;
  background: var(--color-cream);
  overflow: auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-rows: auto;
  gap: 1.5rem;
  place-items: center;
  margin: 0 auto;
  padding-bottom: 4rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    height: 100%;
    gap: 2rem;
    grid-template-columns: 1.75fr 1fr;
    padding: 2rem 2.5rem 4rem;
  }

  @media screen and (min-width: 960px) {
    gap: 5rem;
  }
`;

const ContentLeft = styled.div`
  width: 100%;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    height: 100%;
    border-radius: 0.5rem;
  }
`;

const ContentRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0 1.5rem 1.5rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    gap: 2rem;
    align-items: flex-start;
    padding: 0;
  }
`;

const ContentImgMobile = styled.img`
  display: block;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    display: none;
  }
`;

const ContentImgDesktop = styled.img`
  display: none;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    display: block;
  }
`;

const StyledContainer = styled(Container)`
  padding: 0;
  width: -webkit-fill-available;
`;

const StyledListWrapper = styled(ListWrapper)`
  position: relative;
  margin-top: 1rem;

  &::before {
    border-width: 1px;
    background-color: #fff;
    left: -1.25rem;
  }

  &::after {
    top: 1.125em;
    left: -1rem;
  }

  &.dashed-line::after {
    background: repeating-linear-gradient(
      to bottom,
      var(--color-gray-lighter) 0,
      var(--color-gray-lighter) 4px,
      transparent 4px,
      transparent 8px
    );
  }
`;

const StyledContainerInner = styled.div`
  &:not(:first-child) {
    padding-top: 1rem;
  }
`;

const StyledListElement = styled(ListElement)`
  --check-icon: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none"><path opacity="0.3" d="M13.5 5L6.85355 11.6464C6.65829 11.8417 6.34171 11.8417 6.14645 11.6464L3 8.5" stroke="%236235CC" stroke-width="2" stroke-linecap="round"/></svg>');
  margin: 0 0 0.25rem;

  &:first-child {
    margin-top: 0.5rem;
  }
`;

interface RxDietDescriptionStepProps {
  onContinue?: () => void;
}

const RxDietDescriptionStep: FC<RxDietDescriptionStepProps> = ({
  onContinue
}) => {
  return (
    <PageContainer>
      <DisableAppPadding />
      <ContentGrid>
        <ContentLeft>
          <ContentImgMobile
            src={rxDietDescriptionDialogImgMobile}
            alt=""
            aria-hidden="true"
          />
          <ContentImgDesktop
            src={rxDietDescriptionDialogImgDesktop}
            alt=""
            aria-hidden="true"
          />
        </ContentLeft>

        <ContentRight>
          <div>
            <StyledPill style={{ marginBottom: "0.75rem" }}>
              <small className="as-tiny">
                <Translate msg="poweredBy" />
              </small>
              <IconRxDietLogoPurple />
            </StyledPill>

            <h4 className="as-h4-large">
              <Translate msg="rxDiet.descriptionDialog.content.title" />
            </h4>
          </div>

          <StyledContainer>
            {rxDietDescriptionListItems.map((getListItem, listItemIndex) => {
              const listItem = getListItem();
              return (
                <StyledContainerInner key={listItem.id}>
                  <StyledListWrapper
                    className={
                      listItemIndex === rxDietDescriptionListItems.length - 1
                        ? "dashed-line"
                        : ""
                    }
                  >
                    <h6 className="as-body2">
                      {listItem.title}
                      {listItem.optional && (
                        <>
                          {" "}
                          <small
                            className="as-tiny color-c-60 strong"
                            style={{ textTransform: "lowercase" }}
                          >
                            (<Translate msg="optional" />)
                          </small>
                        </>
                      )}
                    </h6>

                    {Boolean(listItem.subtitles?.length) && (
                      <List className="m0">
                        {listItem.subtitles?.map((subtitle, subtitleIndex) => (
                          <StyledListElement
                            key={`rxdiet-descriptionStep-list-item-${listItemIndex}-subtitle-${subtitleIndex}`}
                            className={subtitleIndex === 0 ? "m0" : ""}
                          >
                            {subtitle}
                          </StyledListElement>
                        ))}
                      </List>
                    )}
                  </StyledListWrapper>
                </StyledContainerInner>
              );
            })}
          </StyledContainer>

          <Button
            theme="rxdiet"
            onPress={() => {
              onContinue?.();
            }}
          >
            <Translate msg="continue" />
          </Button>
        </ContentRight>
      </ContentGrid>
    </PageContainer>
  );
};

export default RxDietDescriptionStep;
